import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { createApp, onUnmounted, ref, watch } from 'vue';

import { useRecommendations } from '~/composables/use-recommendation';
import * as DataDogLogs from '~/lib/datadog/datadog';

import { BaseSection } from './base-section';

gsap.registerPlugin(ScrollTrigger);

/**
 * Recommendations Carousel
 *
 * This section displays a carousel with recommendations provided by DY
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class RecommendationsCarousel extends BaseSection {
  constructor() {
    super('[data-section-type="recommendations-carousel"]');
  }

  init(instanceData) {
    const container = instanceData.container;
    const templateName = container.dataset.sectionTemplateName || '';

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Recommendations Carousel - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return null;
    }

    if (!templateName) {
      DataDogLogs.error(
        '[Allbirds section] - Recommendations Carousel - init - No template name provided',
        {},
        new Error('No template name provided'),
      );
      return null;
    }

    createApp({
      name: 'RecommendationsCarouselSection',
      setup() {
        const carousel = ref(null);
        const {
          displayOptions,
          hasError,
          hasRecommendations,
          isLoading,
          recommendations,
          reportRecommendationClick,
        } = useRecommendations(templateName);

        const handleRecommendationClick = async (event) => {
          const item = {
            slotId: event.target.dataset.slotId,
            url: event.target.dataset.url,
          };
          await reportRecommendationClick(item, event);
        };

        const getSwiperSettings = (recCount) => ({
          breakpoints: {
            0: {
              slidesPerView: 1.3,
            },
            1024: {
              slidesPerView: 3.6,
            },
            768: {
              slidesPerView: 2.6,
            },
          },
          loop: recCount >= 5,
          modules: [Navigation],
          navigation: {
            nextEl: '.swiper-next-button',
            prevEl: '.swiper-prev-button',
          },
          roundLengths: true,
          slidesOffsetAfter: 10,
          slidesOffsetBefore: 10,
          spaceBetween: 10,
          updateOnWindowResize: true,
        });

        const initSwiper = () => {
          const swiperContainer = container.querySelector('.swiper');
          if (swiperContainer) {
            carousel.value = new Swiper(
              swiperContainer,
              getSwiperSettings(recommendations.value.length),
            );
          }
        };

        const displayRecommendations = () => {
          gsap.fromTo(
            container,
            { opacity: 0 },
            {
              duration: 0.5,
              opacity: 1,
              scrollTrigger: {
                end: 'bottom 20%',
                once: true,
                start: 'top 80%',
                trigger: container,
              },
            },
          );
        };

        watch(isLoading, () => {
          if (!isLoading.value && recommendations?.value?.length > 0) {
            initSwiper();
            displayRecommendations();
          } else if (!isLoading.value && !recommendations?.value?.length) {
            container.classList.add('hidden');
          }
        });

        onUnmounted(() => {
          carousel.value?.destroy();
          carousel.value = null;
        });

        return {
          displayOptions,
          handleRecommendationClick,
          hasError,
          hasRecommendations,
          isLoading,
          recommendations,
        };
      },
    }).mount(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new RecommendationsCarousel();
});
